import {
  Button,
  ButtonProps,
  Icon,
  IconButton,
  SxProps,
  visuallyHidden,
} from "@outschool/backpack";
import { faArrowTurnRight } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  Modal,
  TrackingProperties,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import { Screen, useNodeRef } from "@outschool/ui-utils";
import React from "react";

export type ShareMenuProps = React.PropsWithChildren<
  Omit<ShareButtonProps, "sx" | "onClick"> & {
    buttonSx?: ShareButtonProps["sx"];
  }
>;

export default function ShareMenu({
  children,
  variant,
  size,
  label,
  hideLabelOnMobile,
  iconOnly,
  linkColor,
  trackingMetaData,
  showIcon,
  buttonSx,
}: ShareMenuProps) {
  const [isShareBoxVisible, setIsSharedBoxvisable] = React.useState(false);

  const toggleShare = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      setIsSharedBoxvisable(oldValue => !oldValue);
    },
    [],
  );

  const closeShare = React.useCallback(() => {
    setIsSharedBoxvisable(false);
  }, []);

  return (
    <React.Fragment>
      <ShareButton
        iconOnly={iconOnly}
        variant={variant}
        size={size}
        label={label}
        hideLabelOnMobile={hideLabelOnMobile}
        onClick={toggleShare}
        linkColor={linkColor}
        trackingMetaData={trackingMetaData}
        showIcon={showIcon}
        sx={buttonSx}
      />
      <Modal fullBleed={false} open={isShareBoxVisible} onClose={closeShare}>
        {children}
      </Modal>
    </React.Fragment>
  );
}

interface ShareButtonProps {
  onClick: (e: React.SyntheticEvent<Element, Event>) => void;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  iconOnly?: boolean;
  label?: string;
  hideLabelOnMobile?: boolean;
  linkColor?: string;
  showIcon?: boolean;
  trackingMetaData?: TrackingProperties;
  sx?: SxProps;
}

export function ShareButton({
  variant,
  size,
  onClick,
  iconOnly,
  label,
  hideLabelOnMobile = false,
  linkColor = "inherit",
  showIcon = true,
  trackingMetaData,
  sx = {},
}: ShareButtonProps) {
  const { t } = useTranslation("ui-components-website\\ShareMenu");
  if (!label) {
    label = t`Share`;
  }
  const isMobile = Screen.useIsMobile();
  const [shareNode, shareRef] = useNodeRef();
  const { trackTouch: trackShareTouch } = useImpressionTracking({
    node: shareNode,
    trackingLabel: "share_button",
    uniqueId: "share_button",
    sharedProperties: trackingMetaData,
  });

  const handleClick = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>) => {
      trackShareTouch();
      onClick(e);
    },
    [onClick, trackShareTouch],
  );

  const iconButtonSize =
    size === "inherit" || size === undefined ? undefined : size;

  const shouldHideLabel = hideLabelOnMobile && isMobile;

  return iconOnly ? (
    <IconButton
      ref={shareRef as any}
      icon={faArrowTurnRight}
      aria-label={label}
      onClick={handleClick}
      size={iconButtonSize as any}
      variant="text"
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
      data-test-id="share-menu-button"
    />
  ) : (
    <Button
      variant={variant}
      sx={[
        {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      ref={shareRef}
      size={size}
      onClick={handleClick}
      aria-label={label}
      data-test-id="share-menu-button"
      startIcon={
        showIcon ? (
          <Icon
            sx={{
              color: linkColor,
            }}
            icon={faArrowTurnRight}
            data-test-id="share-menu-icon"
          />
        ) : undefined
      }
    >
      {!shouldHideLabel ? (
        <>{label}</>
      ) : (
        <span style={visuallyHidden}>{label}</span>
      )}
    </Button>
  );
}
